import {
  formatOptionFieldValue,
  getCountryName,
  isOptionFieldType,
  LOCALE_NAMES,
  parseDbTimestamp,
} from "@repo/shared";
import type { LocaleCode } from "@repo/shared/types";
import { fieldAppliesToClient, type Translated } from "@repo/shared/types";
import { useQueryClient } from "@tanstack/react-query";
import { Building2, Eye, EyeOff, Loader2, Star, User, Users } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router";
import { type ActivityDateFieldValue } from "@/components/clients/activity-date-fields";
import { ClientAccessDeniedCard } from "@/components/clients/client-access-denied-card";
import { ClientAccessLock } from "@/components/clients/client-access-lock";
import { PageHeader } from "@/components/page-header";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { DocButton } from "@/components/ui/doc-button";
import { EmptyState } from "@/components/ui/empty-state";
import { TimeAgoTooltip } from "@/components/ui/time-ago-tooltip";
import { useClientIdentity, VerifiedIdBadge, VerifiedIdDialog } from "@/components/verified-id";
import { useConfirm } from "@/hooks/use-confirm";
import { ApiError, del, post } from "@/lib/api/_client";
import { clientAccessDenialOf } from "@/lib/api/client-access-denial";
import { formatDateTimeFieldValue } from "@/lib/date-utils";
import { useClientAccessSettings } from "@/lib/hooks/use-app-bootstrap";
import { useClientCostCategories } from "@/lib/hooks/use-client-cost-categories";
import { useClient, useClientAccounts, useClientSavingsPlans } from "@/lib/hooks/use-client-detail";
import {
  useClientTabs,
  useDisplayDates,
  useEditFields,
  useFavouritedClientIds,
  useProfileFields,
} from "@/lib/hooks/use-clients";
import { useInvalidateClientAccessData } from "@/lib/hooks/use-teams";
import { useUserLanguage } from "@/lib/i18n";
import { useLocalize } from "@/lib/i18n/use-localize";
import { useT } from "@/lib/i18n/use-t";
import { useRegionalFormat } from "@/lib/regional-format/context";
import { displayUserName } from "@/lib/user-display";
import { localCalendarDayOffset } from "@/lib/utils";
import { ClientDialog } from "../client-dialog";
import { ClientTabs } from "./client-tabs";
import { ACTIVITY_DATE_LABELS, T } from "./client-view-content.i18n";
import { FollowupSelectionDialog } from "./followup-selection-dialog";

// Format a date as "Today", "X days ago", "X months ago", etc.
function formatTimeAgo(date: Date | string | null, t: Translated<typeof T>): string | null {
  if (!date) return null;

  // QL-494 — server timestamps carry no UTC marker; `new Date()` would read
  // them as local time and shift them by the browser's UTC offset.
  const offset = localCalendarDayOffset(date);
  if (offset === null) return null;
  // A future timestamp (clock skew) counts as today.
  const diffDays = Math.max(0, -offset);

  if (diffDays === 0) return t.today;
  if (diffDays === 1) return t.yesterday;
  if (diffDays < 30) return t.daysAgo(diffDays);

  const diffMonths = Math.floor(diffDays / 30);
  if (diffMonths === 1) return t.monthAgo;
  if (diffMonths < 12) return t.monthsAgo(diffMonths);

  const diffYears = Math.floor(diffMonths / 12);
  if (diffYears === 1) return t.yearAgo;
  return t.yearsAgo(diffYears);
}

function maskSSN(ssn: string) {
  if (!ssn || ssn.length < 4) return ssn;
  const last4 = ssn.slice(-4);
  return `***-**-${last4}`;
}

type ClientViewContentProps = {
  clientId: string;
};

export function ClientViewContent({ clientId }: ClientViewContentProps) {
  const navigate = useNavigate();
  const { userLanguage } = useUserLanguage();
  const localize = useLocalize();
  const { formatDateTime } = useRegionalFormat();
  const accessSettings = useClientAccessSettings();
  const { clientCostCategories } = useClientCostCategories();
  const t = useT(T);
  const tDates = useT(ACTIVITY_DATE_LABELS);

  // Data hooks
  const {
    client,
    error: clientError,
    isLoading: clientLoading,
    mutate: mutateClient,
  } = useClient(clientId);
  const denial = clientAccessDenialOf(clientError);
  // QL-199 — the profile card renders the "Show in profile" set, not the
  // list-visible set /active serves.
  const { profileFields } = useProfileFields();
  // The edit dialog needs every active field with its tab assignment, not just
  // the profile-visible subset the profile card renders.
  const { editFields } = useEditFields();
  const { clientTabs } = useClientTabs();
  const { favouritedClientIds, mutate: mutateFavourites } = useFavouritedClientIds();
  const { displayDates } = useDisplayDates();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [identityOpen, setIdentityOpen] = useState(false);
  // `clientId` (the route param), NOT `client.id`: this runs ABOVE the
  // `!client` loading guard below, so reading off `client` crashes on first
  // render. The param is always present and lets the query start immediately.
  const { identity } = useClientIdentity(clientId);
  const queryClient = useQueryClient();
  const invalidateClientAccessData = useInvalidateClientAccessData();
  const [isPending, setIsPending] = useState(false);
  const [ssnVisible, setSsnVisible] = useState(false);
  // QL-150 phase 2 (D4) — client-level entry to the unified follow-up dialog.
  // Rendered only when there is anything followable at all (>=1 active plan
  // or synced account — the same advisor-visible sets the tabs show); the
  // fine-grained per-flow eligibility stays with the dialog and the server.
  // Same query keys as ClientTabs, so react-query dedupes the fetches.
  const [followupOpen, setFollowupOpen] = useState(false);
  const { accounts: followableAccounts } = useClientAccounts(clientId);
  const { savingsPlans: followablePlans } = useClientSavingsPlans(clientId);
  const hasFollowableItems =
    (followableAccounts?.length ?? 0) > 0 || (followablePlans?.length ?? 0) > 0;
  const { confirm, ConfirmationDialog } = useConfirm();

  const isFavourited = favouritedClientIds.includes(clientId);

  if (denial) return <ClientAccessDeniedCard denial={denial} />;

  if (clientError && !client) {
    const notFound = clientError instanceof ApiError && clientError.status === 404;
    return <EmptyState>{notFound ? t.clientNotFound : t.clientLoadFailed}</EmptyState>;
  }

  if (clientLoading || !client) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const handleEdit = () => {
    setDialogOpen(true);
  };

  const handleToggleFavourite = async () => {
    setIsPending(true);
    try {
      await post(`/internal/clients/${clientId}/favourite`);
      mutateFavourites();
    } catch {
      // Silently ignore favourite toggle errors
    } finally {
      setIsPending(false);
    }
  };

  const handleDelete = async () => {
    const confirmed = await confirm({
      title: t.deleteClient,
      description: t.deleteConfirmation(client.name),
      confirmText: t.delete,
      cancelText: t.cancel,
      variant: "destructive",
    });

    if (!confirmed) return;

    setIsPending(true);
    try {
      await del(`/internal/clients/${clientId}`);
      invalidateClientAccessData();
      navigate("/clients");
    } catch {
      await confirm({
        title: t.error,
        description: t.failedToDelete,
        confirmText: t.ok,
        variant: "destructive",
        type: "alert",
      });
    } finally {
      setIsPending(false);
    }
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    mutateClient();
    // QL-f — editing name, national id, country or date of birth voids the
    // Verified ID, and that is resolved server-side. Without this the badge
    // stays on screen until a reload, which reads as the feature not working.
    queryClient.invalidateQueries({ queryKey: ["client-identity", clientId] });
  };

  const getFieldValue = (field: any) => {
    if (field.isStandard) {
      const fieldMap: Record<string, any> = {
        "standard-email": client.email,
        "standard-mobilePhone": client.mobilePhone,
        "standard-ssn": client.ssn ? (ssnVisible ? client.ssn : maskSSN(client.ssn)) : null,
        "standard-dateOfBirth": client.dateOfBirth,
        "standard-orgId": client.orgId,
        "standard-country": client.country ? getCountryName(client.country, userLanguage) : null,
        "standard-language": client.language
          ? LOCALE_NAMES[client.language as LocaleCode] || client.language
          : null,
        "standard-owner": displayUserName(client.owner, userLanguage),
        "standard-externalId": client.externalId,
        // QL-126 — the client stores the id; the profile shows the name.
        "standard-costCategory": client.clientCostCategoryId
          ? localize(
              clientCostCategories.find((c) => c.id === client.clientCostCategoryId)?.nameI18n,
            ) ||
            "" ||
            null
          : null,
        // QL-380 — teams are labels too; shown whatever the access settings.
        "standard-teams":
          (client.teams ?? []).map((team: { name: string }) => team.name).join(", ") || null,
      };
      return fieldMap[field.id] || null;
    }

    const value = client.customValues?.find((cv: any) => cv.customFieldId === field.id);
    if (isOptionFieldType(field.type) && value?.value) {
      // Stored values are option ids (QL-123) — resolve to localized labels.
      return formatOptionFieldValue(field.type, value.value, field.options, userLanguage) || null;
    }
    if (field.type === "boolean" && value?.value) {
      return value.value === "true" ? t.yes : t.no;
    }
    if (field.type === "datetime" && value?.value) {
      return formatDateTimeFieldValue(value.value, formatDateTime);
    }
    return value?.value || null;
  };

  const filteredCustomFields = profileFields.filter((field) =>
    fieldAppliesToClient(field.clientType, client.type),
  );

  const fieldsWithValues = filteredCustomFields.filter((field: any) => {
    const value = getFieldValue(field);
    return value !== null && value !== "";
  });

  const hasAnyFieldValues = fieldsWithValues.length > 0;

  return (
    <>
      <div className="space-y-6">
        <PageHeader
          breadcrumbs={[{ label: t.clients, href: "/clients" }, { label: client.name }]}
          title={
            <span className="flex items-center gap-3">
              {client.type === "Company" ? (
                <Building2 className="h-8 w-8 text-muted-foreground" />
              ) : client.type === "Group" ? (
                <Users className="h-8 w-8 text-muted-foreground" />
              ) : (
                <User className="h-8 w-8 text-muted-foreground" />
              )}
              {client.name}
              <ClientAccessLock
                settings={accessSettings}
                isPrivate={client.isPrivate ?? false}
                restriction={client.accessRestriction}
                ownerName={client.owner?.name}
                teamNames={(client.teams ?? []).map((team: { name: string }) => team.name)}
                className="h-4 w-4"
              />
              {/* QL-e — immediately after the name it belongs to. Renders
                  nothing when there is no valid verification. */}
              <VerifiedIdBadge
                summary={identity?.summary}
                size="lg"
                onOpen={() => setIdentityOpen(true)}
              />
              <button
                type="button"
                onClick={handleToggleFavourite}
                disabled={isPending}
                className="text-muted-foreground hover:text-warning transition-colors disabled:opacity-50"
                aria-label={isFavourited ? t.removeFromFavourites : t.addToFavourites}
              >
                <Star className={`h-6 w-6 ${isFavourited ? "fill-warning text-warning" : ""}`} />
              </button>
            </span>
          }
          description={
            displayDates.length > 0 ? (
              <span className="text-sm flex flex-wrap items-center">
                {displayDates.map((field: string, index: number) => {
                  const dateValue = client[field as keyof typeof client] as string | null;
                  const formattedDate = formatTimeAgo(dateValue, t);
                  const label = tDates[field as ActivityDateFieldValue] ?? field;

                  return (
                    <span key={field} className="flex items-center">
                      {index > 0 && <span className="mx-2 text-muted-foreground/50">•</span>}
                      {label}:{" "}
                      {dateValue && formattedDate ? (
                        <TimeAgoTooltip date={parseDbTimestamp(dateValue)}>
                          {formattedDate}
                        </TimeAgoTooltip>
                      ) : (
                        formattedDate || t.never
                      )}
                    </span>
                  );
                })}
              </span>
            ) : (
              <span className="text-sm">
                {t.created}{" "}
                {client.createdAt ? (
                  <TimeAgoTooltip date={parseDbTimestamp(client.createdAt)}>
                    {formatTimeAgo(client.createdAt, t)}
                  </TimeAgoTooltip>
                ) : null}
              </span>
            )
          }
          actions={
            <>
              <DocButton docSlug="client-profiles" variant="outline" size="sm" className="gap-2">
                {t.documentation}
              </DocButton>
              {hasFollowableItems && (
                <Button
                  variant="outline"
                  onClick={() => setFollowupOpen(true)}
                  disabled={isPending}
                >
                  {t.followUp}
                </Button>
              )}
              <Button variant="outline" onClick={handleEdit} disabled={isPending}>
                {t.edit}
              </Button>
              <Button variant="destructive" onClick={handleDelete} loading={isPending}>
                {t.delete}
              </Button>
            </>
          }
        />

        {filteredCustomFields.length > 0 && (
          <Card>
            <CardContent className="pt-6">
              {hasAnyFieldValues ? (
                <dl className="grid gap-x-12 gap-y-4 sm:grid-cols-2 lg:grid-cols-3">
                  {fieldsWithValues.map((field: any) => (
                    <div key={field.id} className="flex flex-col gap-0.5">
                      <dt className="text-xs font-medium text-muted-foreground">
                        {localize(field.nameI18n) || field.name}
                      </dt>
                      <dd className="flex items-center gap-2 text-sm">
                        <span className={field.id === "standard-ssn" ? "font-mono" : ""}>
                          {getFieldValue(field)}
                        </span>
                        {field.id === "standard-ssn" && (
                          <button
                            type="button"
                            onClick={() => setSsnVisible(!ssnVisible)}
                            className="text-muted-foreground hover:text-foreground transition-colors"
                            aria-label={ssnVisible ? t.hideSSN : t.showSSN}
                          >
                            {ssnVisible ? (
                              <EyeOff className="h-4 w-4" />
                            ) : (
                              <Eye className="h-4 w-4" />
                            )}
                          </button>
                        )}
                      </dd>
                    </div>
                  ))}
                </dl>
              ) : (
                <p className="text-sm text-muted-foreground text-center py-4">
                  {t.noAdditionalInfo}
                </p>
              )}
            </CardContent>
          </Card>
        )}

        <ClientTabs
          clientId={clientId}
          clientType={client.type || "Person"}
          clientCountry={client.country}
        />
      </div>

      <VerifiedIdDialog
        open={identityOpen}
        onClose={() => setIdentityOpen(false)}
        verifications={identity?.verifications ?? []}
      />

      <ClientDialog
        open={dialogOpen}
        client={client}
        customFields={editFields}
        clientTabs={clientTabs}
        onClose={handleDialogClose}
      />
      <FollowupSelectionDialog
        clientId={clientId}
        target={followupOpen ? { kind: "client" } : null}
        onClose={() => setFollowupOpen(false)}
        onError={(message) => {
          void confirm({
            title: t.error,
            description: message,
            confirmText: t.ok,
            variant: "destructive",
            type: "alert",
          });
        }}
      />
      <ConfirmationDialog />
    </>
  );
}
